// Corpus-DF harness-template mining over a window of raw prompt
// payloads. Pure + deterministic, no DOM, no harness-specific rules:
//
//   • Every line is NORMALIZED — volatile tokens (urls, uuids, hex ids,
//     absolute paths, numbers) mask to placeholders, so "session
//     heartbeat 41" and "session heartbeat 387" become the same key.
//   • A normalized line that recurs across enough DISTINCT SESSIONS is
//     scaffolding. Session-level document frequency is the load-bearing
//     choice: every api_request re-sends the conversation so far, so
//     within one session a user's actual words repeat in every
//     subsequent payload — only cross-session recurrence separates the
//     harness's voice from the human's.
//   • Consecutive scaffolding lines group into BLOCKS (a whole injected
//     <system-reminder>, a tool-result banner, a system-prompt section),
//     which is the unit the lens renders.
//
// The miner's eventual consumer is the trace detail screen — knowing
// which lines of a payload are scaffolding is what lets the UI dim
// harness traffic and foreground what the person actually typed.

const URL_RE = /https?:\/\/\S+/g;
const UUIDISH_RE = /\b[0-9a-fA-F][0-9a-fA-F-]{14,}\b/g;
const HEX_RE = /\b[0-9a-f]{7,}\b/g;
// The lookbehind matters: a '/' preceded by '<' is a closing XML tag,
// not a path — without it '</system-reminder>' masks to '<<p>>' and
// drops below the eligibility floor, making closing tags unstrippable.
const ABS_PATH_RE = /(?<![<\w])(?:[A-Za-z]:)?\/[^\s'"`|;&<>:$()*{}[\]=,]+/g;
// Windows backslash paths — the fleet is heavily Windows, and the
// unix-only mask above left every C:\Users\... line volatile, so the
// envelope lines carrying them never recurred.
const WIN_PATH_RE = /(?<![<\w])[A-Za-z]:\\[^\s'"`|;&<>:$()*{}[\]=,]+/g;
// Mixed-alphanumeric ids (base62 tool_use ids like toolu_01NN…, base36
// task ids, worktree slugs, ISO dates): any token ≥8 chars carrying a
// digit. Prose never carries digits, so real words survive; pure hex
// is already <h> by the time this runs.
const ALNUM_ID_RE = /\b(?=[a-z0-9_-]*\d)[a-z0-9_-]{8,}\b/g;
const NUM_RE = /\d+/g;
const WS_RE = /\s+/g;
const QUOTED_RE = /"[^"]{0,300}"/g;
// Pure prose (letters, spaces, basic punctuation) never rides the
// adjacency rescue below — a human's repeated steering line that
// happens to sit next to scaffolding must not get dragged in.
const PURE_PROSE_RE = /^[\p{L}\s.,'!?-]*$/u;

/** Normalize one raw line to its template key. */
export function normTemplateLine(line) {
    let s = String(line).trim().toLowerCase();
    // Quoted payload slots in prose lines — harness messages constantly
    // quote the variable bit ('Background command "X" completed'). Only
    // when enough prose remains OUTSIDE the quotes: JSON-structure
    // lines ('"key": "value"') and fully-quoted lines keep their quotes,
    // so structural JSON doesn't collapse into one universal key.
    if (s.includes('"')) {
        const outside = s.replace(QUOTED_RE, " ");
        const letters = (outside.match(/\p{L}/gu) || []).length;
        if (letters >= 8) s = s.replace(QUOTED_RE, "<q>");
    }
    s = s.replace(URL_RE, "<u>");
    s = s.replace(UUIDISH_RE, "<h>");
    s = s.replace(ABS_PATH_RE, "<p>");
    s = s.replace(WIN_PATH_RE, "<p>");
    s = s.replace(HEX_RE, "<h>");
    s = s.replace(ALNUM_ID_RE, "<h>");
    s = s.replace(NUM_RE, "0");
    return s.replace(WS_RE, " ");
}

// Short pure-alpha lines are exempt from stripping: repeated human
// steering ("continue", "yes do that") must survive DF. Eligibility
// length is judged on the RAW line — masking can shrink machine lines
// (tags, ids) below any key-length floor.
export function templateEligible(raw, key) {
    if (key.length >= 12) return true;
    return String(raw).trim().length >= 6 && !/^[\p{L}]+$/u.test(key.replace(/ /g, ""));
}

/**
 * Pre-split one payload's input-side text into mining lines. Done per
 * doc (in the hydration worker, between fetches) so the corpus pass
 * doesn't re-normalize hundreds of payloads in one synchronous burst.
 * @returns {{raw: string, key: string, eligible: boolean}[]}
 */
export function splitTemplateLines(text) {
    return String(text || "").split("\n").map((raw) => {
        const key = normTemplateLine(raw);
        return { raw, key, eligible: templateEligible(raw, key) };
    });
}

/**
 * Mine recurring harness blocks from a window of prompt payloads.
 *
 * @param docs [{id, sessionId, lines?, text?, chars?}] — `lines` from
 *   splitTemplateLines (or `text` to split here); `chars` the payload's
 *   input-side text length (defaults to the sum of line lengths).
 * @returns {{
 *   templates: {id, key, raw, lineCount, sessions, prompts, promptIds: Set, chars}[],
 *   threshold: number, sessionCount: number,
 *   strippedChars: number, totalChars: number,
 * }}
 */
export function mineHarnessTemplates(docs, { minDf = 3, frac = 0.04 } = {}) {
    const prepped = (docs || []).map((d) => ({
        id: d.id,
        sessionId: String(d.sessionId ?? ""),
        lines: d.lines ?? splitTemplateLines(d.text),
        chars: d.chars ?? (d.text != null
            ? String(d.text).length
            : (d.lines ?? []).reduce((n, ln) => n + ln.raw.length + 1, 0)),
    }));

    const sessionIds = new Set(prepped.map((d) => d.sessionId));
    const lineSessions = new Map();
    const lineVariants = new Map(); // key → Set of distinct raw forms (capped)
    for (const d of prepped) {
        for (const ln of d.lines) {
            if (!ln.eligible) continue;
            let s = lineSessions.get(ln.key);
            if (!s) lineSessions.set(ln.key, (s = new Set()));
            s.add(d.sessionId);
            let v = lineVariants.get(ln.key);
            if (!v) lineVariants.set(ln.key, (v = new Set()));
            if (v.size < 4) v.add(ln.raw.trim());
        }
    }
    let threshold = Math.max(minDf, Math.ceil(frac * sessionIds.size));
    // Small windows: a floor of minDf with only minDf sessions demands
    // UNANIMITY — one alien session in the sample (a different agent,
    // a different harness) vetoes every template. Once ≥3 sessions
    // exist, cap the threshold at n-1 (majority at n=3) so a single
    // odd session can't zero the set.
    if (sessionIds.size >= 3) threshold = Math.min(threshold, Math.max(2, sessionIds.size - 1));
    const boiler = new Set();
    for (const [k, s] of lineSessions) if (s.size >= threshold) boiler.add(k);

    // Slot-variant evidence: the same masked key carrying ≥3 DISTINCT
    // raw forms means a machine filled the slots (ids, paths, quoted
    // payloads) differently each time — humans repeat themselves
    // verbatim, so re-sent history never inflates this. Catches
    // scaffolding session-DF can't see: features only one or two
    // sampled sessions used (background-task notifications, etc.).
    for (const [k, v] of lineVariants) if (v.size >= 3) boiler.add(k);

    // Adjacency rescue: the invariant lines of a slot-filled envelope
    // (<task-notification>, <status>completed</status>) have one raw
    // form and may span too few sessions, but they ride next to boiler
    // lines in ≥80% of their ≥3 occurrences. Pure-prose keys are
    // excluded — a human's repeated line beside scaffolding must not
    // get dragged in. Two passes resolve tag→tag chains.
    for (let iter = 0; iter < 2; iter++) {
        const occ = new Map();
        const adj = new Map();
        for (const d of prepped) {
            const L = d.lines;
            for (let i = 0; i < L.length; i++) {
                const ln = L[i];
                if (!ln.eligible || boiler.has(ln.key) || PURE_PROSE_RE.test(ln.key)) continue;
                occ.set(ln.key, (occ.get(ln.key) || 0) + 1);
                const prevB = i > 0 && L[i - 1].eligible && boiler.has(L[i - 1].key);
                const nextB = i + 1 < L.length && L[i + 1].eligible && boiler.has(L[i + 1].key);
                if (prevB || nextB) adj.set(ln.key, (adj.get(ln.key) || 0) + 1);
            }
        }
        let added = 0;
        for (const [k, n] of occ) {
            if (n >= 3 && (adj.get(k) || 0) / n >= 0.8) {
                boiler.add(k);
                added += 1;
            }
        }
        if (!added) break;
    }

    // Second pass: group each doc's consecutive boiler lines into runs
    // and aggregate runs by their joined key. A block recurring several
    // times inside one payload (re-sent history) counts its chars each
    // time — that is honestly what scaffolding costs the payload — but
    // sessions/prompts are sets, so reach stats don't self-inflate.
    const blocks = new Map();
    let strippedChars = 0;
    let totalChars = 0;
    for (const d of prepped) {
        totalChars += d.chars;
        let run = null;
        const flush = () => {
            if (!run) return;
            const key = run.keys.join("\n");
            let b = blocks.get(key);
            if (!b) {
                b = { key, raw: run.raws.join("\n"), lineCount: run.keys.length, sessionSet: new Set(), promptIds: new Set(), chars: 0 };
                blocks.set(key, b);
            }
            b.sessionSet.add(d.sessionId);
            b.promptIds.add(d.id);
            b.chars += run.chars;
            strippedChars += run.chars;
            run = null;
        };
        for (const ln of d.lines) {
            if (ln.eligible && boiler.has(ln.key)) {
                if (!run) run = { keys: [], raws: [], chars: 0 };
                run.keys.push(ln.key);
                run.raws.push(ln.raw);
                run.chars += ln.raw.length + 1;
            } else {
                flush();
            }
        }
        flush();
    }

    const templates = [...blocks.values()]
        .sort((a, b) => (b.sessionSet.size - a.sessionSet.size) || (b.chars - a.chars) || a.key.localeCompare(b.key))
        .map((b, i) => ({
            id: `t${i + 1}`,
            key: b.key,
            raw: b.raw,
            lineCount: b.lineCount,
            sessions: b.sessionSet.size,
            prompts: b.promptIds.size,
            promptIds: b.promptIds,
            chars: b.chars,
        }));

    return { templates, threshold, sessionCount: sessionIds.size, strippedChars, totalChars };
}

/**
 * Flatten a mined result's blocks back to the per-LINE key set — the
 * shape the trace-detail overlay classifies against (line in, verdict
 * out; block grouping is a display concern).
 */
export function boilerKeySet(mined) {
    const keys = new Set();
    for (const t of mined?.templates ?? []) {
        for (const k of t.key.split("\n")) keys.add(k);
    }
    return keys;
}

/**
 * Classify one text against a boiler key set, merging consecutive
 * same-class lines into segments. Lossless: concatenating the
 * segments' text reproduces the input exactly, so a renderer can wrap
 * each segment without disturbing the document.
 * @returns {{boiler: boolean, text: string}[]}
 */
export function classifyHarnessSegments(text, boilerKeys) {
    const s = String(text ?? "");
    if (!boilerKeys || boilerKeys.size === 0) return [{ boiler: false, text: s }];
    const lines = s.split("\n");
    const segs = [];
    for (const raw of lines) {
        const key = normTemplateLine(raw);
        const boiler = templateEligible(raw, key) && boilerKeys.has(key);
        const last = segs[segs.length - 1];
        if (last && last.boiler === boiler) last.lines.push(raw);
        else segs.push({ boiler, lines: [raw] });
    }
    return segs.map((seg, i) => ({
        boiler: seg.boiler,
        text: seg.lines.join("\n") + (i < segs.length - 1 ? "\n" : ""),
    }));
}

// A tool_result's content is a string OR an array of blocks.
// Deliberately a local copy of `parseApiRequest.toolResultText`. This module is
// zero-import by design — pure, deterministic, no DOM and no dependency on the
// parser — and coupling it to the parser to save nine lines would cost that. If a
// third copy ever wants writing, export this one instead.
function resultText(content) {
    if (typeof content === "string") return content;
    if (Array.isArray(content)) {
        return content
            .filter((b) => b && b.type === "text" && typeof b.text === "string")
            .map((b) => b.text)
            .join("\n");
    }
    return "";
}

/**
 * The INPUT side of a parsed request (parseApiRequest turns): system +
 * user text and tool results — everything fed TO the model. Assistant
 * turns are the model's own output, not harness traffic, so they stay
 * out of the mining corpus.
 */
export function inputSideText(turns) {
    const parts = [];
    for (const t of turns || []) {
        if (t.role !== "system" && t.role !== "user") continue;
        for (const b of t.blocks || []) {
            if (b.type === "text" && typeof b.text === "string" && b.text) {
                parts.push(b.text);
            } else if (b.type === "tool_result") {
                const txt = resultText(b.content);
                if (txt) parts.push(txt);
            }
        }
    }
    return parts.join("\n");
}
